import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

export default function LimitReachedModal({ isOpen, onClose, type }) {
    const navigate = useNavigate();
    const { loginWithRedirect } = useAuth0();

    if (!isOpen) return null;

    const isGuest = type === 'guest';

    const handlePrimaryAction = () => {
        if (isGuest) {
            loginWithRedirect({
                authorizationParams: {
                    screen_hint: 'signup',
                },
                appState: { returnTo: window.location.pathname },
            });
        } else {
            navigate('/pricing');
            onClose();
        }
    };

    const handleSecondaryAction = () => {
        if (isGuest) {
            loginWithRedirect({
                appState: { returnTo: window.location.pathname },
            });
        } else {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/65 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="glass-strong max-w-md w-full p-6 relative animate-in zoom-in-95 duration-200">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 btn-ghost px-2 py-2 text-slate-400 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-100"
                    aria-label="Close"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Icon */}
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg border border-slate-200 dark:border-white/10 ${isGuest
                    ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-[0_16px_30px_-20px_rgba(168,85,247,0.70)]'
                    : 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-[0_16px_30px_-20px_rgba(251,191,36,0.70)]'
                    }`}>
                    {isGuest ? (
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    ) : (
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    )}
                </div>

                {/* Content */}
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 text-center mb-2">
                    {isGuest ? 'Guest Limit Reached' : 'Daily Limit Reached'}
                </h2>
                <p className="text-slate-600 dark:text-slate-300 text-center mb-6">
                    {isGuest
                        ? 'You\'ve used your free guest generations. Create an account to generate more icons and access premium features!'
                        : 'You\'ve hit your daily limit for free generations. Upgrade to Pro for unlimited icon generations and premium features!'}
                </p>

                {/* Buttons */}
                <div className="space-y-3">
                    <button
                        onClick={handlePrimaryAction}
                        className={`w-full py-3 btn-primary ${!isGuest && 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 border-orange-400/20'}`}
                    >
                        {isGuest ? 'Sign Up Free' : 'Upgrade to Pro'}
                    </button>
                    <button
                        onClick={handleSecondaryAction}
                        className="w-full btn-secondary py-3"
                    >
                        {isGuest ? 'Already have an account? Log in' : 'Maybe later'}
                    </button>
                </div>

                {/* Benefits */}
                <div className="mt-6 pt-6 border-t border-slate-200 dark:border-white/10">
                    <p className="text-xs text-slate-400 text-center mb-3">
                        {isGuest ? 'Unlock full access:' : 'Pro benefits:'}
                    </p>
                    <ul className="space-y-2">
                        <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <svg className="w-4 h-4 text-emerald-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Unlimited icon generations
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <svg className="w-4 h-4 text-emerald-500 dark:text-emerald-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Full quality downloads
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-300">
                            <svg className="w-4 h-4 text-emerald-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Commercial usage rights
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
